import React, { FC } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CoreUtils, IdEnums, ShortCodeEnums, CoreCommonMessages, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { getEnvironmentAwareHref } from '~/core/utils';
import { formatTime } from '~/views/components/gameOrSeriesScoreboard/utils';

import { CommonGCGame, SeasonSeries as SeasonSeriesType, SeasonSeriesTeam, SeasonSeriesWins } from '../../types';

import {
    FullCoverageLink,
    GameContainer,
    GameInfo,
    GameInfoContainer,
    GamesContainer,
    Header,
    HeaderContainer,
    PeriodInfo,
    PeriodTimeContainer,
    PeriodWithIntermission,
    SeasonSeriesContainer,
    SeriesRecord,
    SituationDescriptionChipContainer,
    StartInfo,
    TeamContainer,
    TeamLogoContainer,
    TeamName,
    TeamScore,
    TimeInfo,
} from './styles';

type Props = {
    game: CommonGCGame;
    seasonSeries?: Array<SeasonSeriesType>;
    seasonSeriesWins?: SeasonSeriesWins;
    seasonSeriesFullCoverageUrl?: string;
};

type GameDescriptionProps = {
    game: SeasonSeriesType;
};

type RecordProps = {
    awayTeamAbbrev: ShortCodeEnums.TeamTriCode;
    homeTeamAbbrev: ShortCodeEnums.TeamTriCode;
    seasonSeriesWins: SeasonSeriesWins;
};

type TeamInfoProps = {
    team: SeasonSeriesTeam;
    isTrailing: boolean;
    situationDescriptions?: Array<ShortCodeEnums.SituationCode>;
};

const Record: FC<RecordProps> = ({ awayTeamAbbrev, homeTeamAbbrev, seasonSeriesWins }) => {
    const winningTeam = seasonSeriesWins.homeTeamWins > seasonSeriesWins.awayTeamWins ? homeTeamAbbrev : awayTeamAbbrev;
    const leadingTeamWins = Math.max(seasonSeriesWins.homeTeamWins, seasonSeriesWins.awayTeamWins);
    const trailingTeamWins = Math.min(seasonSeriesWins.homeTeamWins, seasonSeriesWins.awayTeamWins);

    if (leadingTeamWins === trailingTeamWins) {
        return (
            <FormattedMessage
                {...CommonMessages.seriesRecordDescriptions.medium.seriesTies}
                values={{ value: leadingTeamWins }}
            />
        );
    }

    return seasonSeriesWins.neededToWin && leadingTeamWins === seasonSeriesWins.neededToWin ? (
        <FormattedMessage
            {...CommonMessages.seriesRecordDescriptions.long.seriesWinnerLonger}
            values={{
                winningTeam,
                leadingTeamWins,
                trailingTeamWins,
            }}
        />
    ) : (
        <FormattedMessage
            {...CommonMessages.seriesRecordDescriptions.long.seriesLeadingLonger}
            values={{
                winningTeam,
                leadingTeamWins,
                trailingTeamWins,
            }}
        />
    );
};

const TeamInfo: FC<TeamInfoProps> = ({ team, isTrailing, situationDescriptions }) => (
    <TeamContainer>
        <TeamLogoContainer $isTrailing={isTrailing}>
            <TeamDisplay logoUrlOverride={team.logo} team={{ triCode: team.abbrev }} displayName={false} />
        </TeamLogoContainer>
        <TeamName $isTrailing={isTrailing}>{team.abbrev}</TeamName>

        {situationDescriptions && situationDescriptions.length > 0 && (
            <SituationDescriptionChipContainer>
                {situationDescriptions.map((situationDescription) => (
                    <SituationDescriptionChipContainer key={situationDescription}>
                        <CoreUtils.SafeFormattedMessage
                            message={CommonMessages.gameSituationDescriptions.abbreviations[situationDescription]}
                        />
                    </SituationDescriptionChipContainer>
                ))}
            </SituationDescriptionChipContainer>
        )}

        <TeamScore $isTrailing={isTrailing}>{team.score}</TeamScore>
    </TeamContainer>
);

const GameDescription: FC<GameDescriptionProps> = ({ game }) => {
    const intl = useIntl();

    const lastPeriodType = game.gameOutcome?.lastPeriodType;
    const periodDisplay = CoreUtils.getPeriodAndIntermissionDisplay(
        intl,
        game.periodDescriptor,
        game.gameType,
        false,
        game.clock?.inIntermission
    );

    if (CoreUtils.isGameLive(game.gameState)) {
        return (
            <PeriodTimeContainer>
                {game.clock?.inIntermission ? (
                    <PeriodWithIntermission>{periodDisplay}</PeriodWithIntermission>
                ) : (
                    <>
                        <PeriodInfo>{periodDisplay}</PeriodInfo>
                        <TimeInfo>
                            {game.periodDescriptor.periodType !== ShortCodeEnums.PeriodType.shootout &&
                                game.clock?.timeRemaining}
                        </TimeInfo>
                    </>
                )}
            </PeriodTimeContainer>
        );
    }

    if (!lastPeriodType || !CoreUtils.isPostGame(game.gameState)) {
        return (
            <StartInfo>
                {game.gameScheduleState !== ShortCodeEnums.GameScheduleState.scheduled ? (
                    <CoreUtils.SafeFormattedMessage
                        message={CoreCommonMessages.gameScheduleStateDescriptions.abbreviations[game.gameScheduleState]}
                    />
                ) : (
                    formatTime(game.startTimeUTC)
                )}
            </StartInfo>
        );
    }

    return (
        <GameInfo>
            {lastPeriodType === ShortCodeEnums.PeriodType.regulation ? (
                <FormattedMessage
                    {...CoreCommonMessages.gameStateDescriptions.long[ShortCodeEnums.GameState.official]}
                />
            ) : (
                <FormattedMessage
                    description="Describes a game that didn't end in regulation time. Ex. Final/OT, Final/SO"
                    defaultMessage="Final/{periodDisplay}"
                    values={{ periodDisplay }}
                />
            )}
        </GameInfo>
    );
};

const SeasonSeries: FC<Props> = ({ game, seasonSeries, seasonSeriesWins, seasonSeriesFullCoverageUrl }) => {
    const isWebview = useIsWebview();
    const getGamecenterLink = (seasonSeries: SeasonSeriesType): string =>
        game.id === seasonSeries.id && seasonSeries.gameCenterLink
            ? '#'
            : getEnvironmentAwareHref(seasonSeries.gameCenterLink, isWebview);
    const isPlayoffs = seasonSeries?.[0]?.gameType === IdEnums.GameType.playoffs;
    return seasonSeries ? (
        <SeasonSeriesContainer>
            <HeaderContainer>
                <Header role="heading" aria-level={2}>
                    {isPlayoffs ? (
                        <FormattedMessage
                            description="A phrase describing future and completed playoff games between two teams"
                            defaultMessage="Playoff Series"
                        />
                    ) : (
                        <FormattedMessage
                            description="A phrase describing the games two teams have played against each other"
                            defaultMessage="Season Series"
                        />
                    )}
                </Header>
                <SeriesRecord>
                    {seasonSeriesWins && (
                        <Record
                            awayTeamAbbrev={game.awayTeam.abbrev}
                            homeTeamAbbrev={game.homeTeam.abbrev}
                            seasonSeriesWins={seasonSeriesWins}
                        />
                    )}
                </SeriesRecord>
            </HeaderContainer>
            {seasonSeriesFullCoverageUrl && (
                <FullCoverageLink
                    href={getEnvironmentAwareHref(
                        seasonSeriesFullCoverageUrl,
                        isWebview,
                        ShortCodeEnums.Language.English
                    )}
                >
                    <FormattedMessage {...CommonMessages.misc.fullCoverageLink} />
                    <ChevronRightIcon />
                </FullCoverageLink>
            )}
            <GamesContainer>
                {seasonSeries.map((g) => (
                    <GameContainer key={g.id} href={getGamecenterLink(g)} $isCurrentGame={game.id === g.id}>
                        <TeamInfo
                            team={g.awayTeam}
                            isTrailing={!CoreUtils.isGameLive(g.gameState) && g.homeTeam.score > g.awayTeam.score}
                            situationDescriptions={g?.situation?.awayTeam?.situationDescriptions}
                        />
                        <TeamInfo
                            team={g.homeTeam}
                            isTrailing={!CoreUtils.isGameLive(g.gameState) && g.awayTeam.score > g.homeTeam.score}
                            situationDescriptions={g?.situation?.homeTeam?.situationDescriptions}
                        />
                        <GameInfoContainer>
                            <GameDescription game={g} />
                            <GameInfo $isRight>
                                {DateTime.fromISO(g.startTimeUTC)
                                    .setZone(TimeZoneEnums.AMERICA_NEW_YORK)
                                    .toLocaleString({ month: 'short', day: 'numeric' })}
                            </GameInfo>
                        </GameInfoContainer>
                    </GameContainer>
                ))}
            </GamesContainer>
        </SeasonSeriesContainer>
    ) : (
        <></>
    );
};

export default SeasonSeries;
